import { Injectable, Inject, isDevMode, Optional } from '@angular/core';
import { LogsEvent, datadogLogs as datadog } from '@datadog/browser-logs';
import { ENV, Environment } from 'src/environments/environment.provider';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { formatAppVersion } from './version-checker.service';

@Injectable({
    providedIn: 'root'
})
export class LoggerService {
    initialized = false;
    constructor(
        @Optional() @Inject(ENV) private readonly env: Environment,
        @Inject(APP_CONFIG) readonly settings: Settings
    ) {}

    // eslint-disable-next-line @typescript-eslint/require-await
    async init(): Promise<void> {
        if (this.env.production !== true && this.settings.Datadog_Client_TokenRum === 'fake') {
            return;
        }

        try {
            datadog.init({
                clientToken: this.settings.Datadog_Client_Token,
                site: 'us3.datadoghq.com',
                service: this.settings.Datadog_Service,
                forwardErrorsToLogs: true,
                sessionSampleRate: 100,
                env: this.settings.env,
                version: formatAppVersion(this.env.appVersion, true),
                beforeSend: this.beforeSend
            });

            datadog.setGlobalContextProperty('application', 'Nova');

            if (isDevMode()) {
                datadog.logger.setHandler(['console']);
            } else {
                datadog.logger.setHandler(['http']);
            }

            this.initialized = true;
        } catch (err: any) {
            console.log(err.message);
        }
    }

    private beforeSend(log: LogsEvent): boolean {
        // Only want to quiet the URLs we know are cancelled by user action
        const urlsToNotLog = [
            '/ayanova/documents/',
            '/ayanova/compliance/document-upload-tool/candidate-documents/',
            '/facility-contracts/facility-profiles/search'
        ];

        let isLogToBeSent = true;

        // User cancelled GET requests should not be logged as an error
        if (log.http?.url && log.http.status_code === 0 && log.http.method === 'GET') {
            if (urlsToNotLog.some((urlToNotLog) => log?.http?.url.includes(urlToNotLog))) {
                isLogToBeSent = false;
            }
        }

        return isLogToBeSent;
    }

    public debug(message: string, context?: Record<string, any>): void {
        if (this.initialized) {
            datadog.logger.debug(message, context);
        }
    }

    public info(message: string, context?: Record<string, any>): void {
        if (this.initialized) {
            datadog.logger.info(message, context);
        }
    }

    public warn(message: string, context?: Record<string, any>): void {
        if (this.initialized) {
            datadog.logger.warn(message, context);
        }
    }

    public error(message: string, context?: Record<string, any>): void {
        if (this.initialized) {
            datadog.logger.error(message, context);
        }
    }
}
